import { NotFoundError } from '@cian/peperrors/shared';

import {
  ERuntimeType,
  type IRegistryMicrofrontendManifest,
  type IRegistryManifest,
  type IMicrofrontendManifest,
} from '../../../shared/manifest/index.js';
import { Microfrontend } from '../microfrontend/index.js';

import type { MicrofrontendNS } from '../../../shared/microfrontend/index.js';
import type { IRegistry } from '../../../shared/registry/index.js';
import type { PartialBy } from '../../../shared/types/typescript/index.js';
import type { IImage } from '../../image/index.js';
import type { IMicrofrontend } from '../../microfrontend/index.js';
import type { AssetsStorage } from '../assets/AssetsStorage.js';

export class Registry implements IRegistry {
  private readonly microfrontends: Map<MicrofrontendNS.MicrofrontendName, Microfrontend>;

  private readonly assetsStorage: AssetsStorage;

  public constructor(manifest: Omit<IRegistryManifest, 'version'> = {}, assetsStorage: AssetsStorage) {
    const { microfrontends = {} } = manifest;

    this.assetsStorage = assetsStorage;

    this.microfrontends = new Map(
      Object.entries(microfrontends).map(([name, microfrontend]) => [
        name,
        new Microfrontend({ ...microfrontend, name }, this.assetsStorage),
      ]),
    );
  }

  public microfrontend(name: string): IMicrofrontend | undefined {
    return this.microfrontends.get(name);
  }

  public microfrontendStrict(name: string): IMicrofrontend {
    const microfrontend = this.microfrontend(name);
    if (!microfrontend) {
      throw new NotFoundError({
        message: `Микрофронтенд "${name}" не найден в реестре`,
        domain: 'mf-registry.browser.registry',
      });
    }

    return microfrontend;
  }

  public register({
    microfrontendName,
    imageVersion,
    // runtimeName,
    // instanceId,
    // markup,
    microfrontends = {},
  }: IMicrofrontendManifest): IImage {
    Object.entries(microfrontends).forEach(([microfrontendName, microfrontendOptions]) => {
      const microfrontend =
        this.microfrontend(microfrontendName) ||
        this.newMicrofrontend({ ...microfrontendOptions, name: microfrontendName });

      Object.entries(microfrontendOptions.images || {}).forEach(([imageVersion, imageOptions]) => {
        const image =
          microfrontend.image(imageVersion) || microfrontend.newImage({ ...imageOptions, version: imageVersion });

        Object.entries(imageOptions.runtimes || {}).forEach(([runtimeName, runtimeOptions]) => {
          const runtime = image.runtime(runtimeName) || image.newRuntime(runtimeOptions);

          Object.entries(runtimeOptions.instances || {}).forEach(([instanceId, instanceOptions]) => {
            if (runtime.type === ERuntimeType.UI && !runtime.instance(instanceId)) {
              runtime.newInstance({ ...instanceOptions, id: instanceId });
            }
          });
        });
      });
    });

    return this.microfrontendStrict(microfrontendName).imageStrict(imageVersion);
  }

  public newMicrofrontend(manifest: PartialBy<IRegistryMicrofrontendManifest, 'images'>): Microfrontend {
    const microfrontend = new Microfrontend({ images: {}, ...manifest }, this.assetsStorage);

    this.microfrontends.set(microfrontend.name, microfrontend);

    return microfrontend;
  }
}
